import React, { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription,
  Button,
  Input,
  Select,
  Modal,
  Badge,
  DataTable
} from '@besiktning/ui'
import { 
  Plus, 
  Trash2, 
  User as UserIcon,
  Shield,
  Wrench,
  Crown,
  ArrowRightLeft
} from 'lucide-react'
import { garageApi, carCareApi, formatDateTime } from '@besiktning/ui'
import type { User } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'

interface StaffMember {
  id: string
  user: User
  role: string
  active: boolean
  invite_pending?: boolean
  created_at: string
}

export default function Staff() {
  const { currentMembership, memberships } = useAuthStore()
  const queryClient = useQueryClient()
  
  const isCarCare = currentMembership?.type === 'car_care'
  const api = isCarCare ? carCareApi : garageApi
  
  // Determine role from current membership instead of user.garage_memberships
  const isCurrentUserOwner = useMemo(() => {
    if (!currentMembership) return false
    const membership = memberships.find(m => m.id === currentMembership.id && m.type === currentMembership.type)
    return membership?.role === 'owner'
  }, [currentMembership, memberships])
  
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'manager' | 'mechanic'>('mechanic')
  const [memberToRemove, setMemberToRemove] = useState<StaffMember | null>(null)

  const { data: staff = [], isLoading, error } = useQuery<StaffMember[], Error>({
    queryKey: ['staff', isCarCare ? 'car_care' : 'garage'],
    queryFn: () => api.getStaff(),
    retry: false,
  })

  const inviteMutation = useMutation({
    mutationFn: () =>
      garageApi.inviteStaff({
        email: inviteEmail.trim(),
        role: inviteRole,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] })
      setShowInviteModal(false)
      setInviteEmail('')
      setInviteRole('mechanic')
    },
  })

  const inviteErrorMessage = inviteMutation.isError
    ? ((inviteMutation.error as any)?.response?.data?.error as string | undefined) ||
      t('staff.inviteError')
    : null

  const removeMutation = useMutation({
    mutationFn: (userId: string) => garageApi.removeStaff(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] })
      setMemberToRemove(null)
    },
  })

const handleInvite = (e: React.FormEvent) => {
    e.preventDefault()
    inviteMutation.mutate()
  }

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-500" />
      case 'manager':
        return <Shield className="h-4 w-4 text-blue-500" />
      case 'mechanic':
        return <Wrench className="h-4 w-4 text-gray-500" />
      default:
        return <UserIcon className="h-4 w-4 text-gray-500" />
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('staff.roleOwner')
      case 'manager':
        return t('staff.roleManager')
      case 'mechanic':
        return t('staff.roleMechanic')
      default:
        return role
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800'
      case 'manager':
        return 'bg-blue-100 text-blue-800'
      case 'mechanic':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const columns = [
    {
      key: 'user',
      header: t('staff.colName'),
      render: (member: StaffMember) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
            <UserIcon className="h-5 w-5 text-gray-500" />
          </div>
          <div className="ml-3">
            <p className="font-medium">{member.user.name || t('staff.notProvided')}</p>
            <p className="text-sm text-gray-500">{member.user.email || member.user.phone}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('staff.colRole'),
      render: (member: StaffMember) => (
        <div className="flex items-center">
          {getRoleIcon(member.role)}
          <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
            {getRoleLabel(member.role)}
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('staff.colStatus'),
      render: (member: StaffMember) => (
        <Badge variant={member.active ? 'success' : 'default'}>
          {member.invite_pending
            ? t('staff.statusInvited')
            : member.active
              ? t('staff.statusActive')
              : t('staff.statusInactive')}
        </Badge>
      ),
    },
    {
      key: 'added',
      header: t('staff.colAdded'),
      render: (member: StaffMember) => (
        <span className="text-sm text-gray-500">
          {formatDateTime(member.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
      render: (member: StaffMember) => (
        // Only show remove button if current user is owner and member is not owner
        isCurrentUserOwner && member.role !== 'owner' && (
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setMemberToRemove(member)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )
      ),
    },
  ]

  // Check if error is because user is on CarCare membership
  const isCarCareError = (error as any)?.response?.status === 403 && 
    (error as any)?.response?.data?.error?.includes('garage')

  if (isCarCareError) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('staff.heading')}</h1>
          <p className="text-gray-600">
            {t('staff.subtitle')}
          </p>
        </div>

        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xl">✨</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('staff.carCareTitle')}</h3>
                <p className="text-gray-600 mt-1">
                  {t('staff.carCareDescription')}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => {
                    // Scroll to top and open account switcher
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                >
                  <ArrowRightLeft className="mr-2 h-4 w-4" />
                  {t('staff.switchToGarage')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('staff.heading')}</h1>
          <p className="text-gray-600">
            {isCarCare ? t('staff.subtitleCarCare') : t('staff.subtitleGarage')}
          </p>
        </div>
        {!isCarCare && isCurrentUserOwner && (
          <Button onClick={() => setShowInviteModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('staff.invite')}
          </Button>
        )}
      </div>

      {/* Staff Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('staff.staffMembers')}</CardTitle>
          <CardDescription>
            {t('staff.totalMembers', { n: staff.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={staff}
            keyExtractor={(member) => member.id}
            isLoading={isLoading}
            emptyMessage={t('staff.emptyMessage')}
          />
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title={t('staff.invite')}
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setShowInviteModal(false)}
            >
              {t('staff.cancel')}
            </Button>
            <Button
              onClick={handleInvite}
              isLoading={inviteMutation.isPending}
              disabled={!inviteEmail.trim()}
            >
              {t('staff.inviteSubmit')}
            </Button>
          </>
        }
      >
        <form onSubmit={handleInvite}>
          <div className="space-y-4">
            <Input
              label={t('staff.emailLabel')}
              type="email"
              placeholder={t('staff.emailPlaceholder')}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Select
              label={t('staff.roleLabel')}
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'manager' | 'mechanic')}
              options={[
                { value: 'mechanic', label: t('staff.roleMechanic') },
                { value: 'manager', label: t('staff.roleManager') },
              ]}
            />
            <p className="text-sm text-gray-500">
              {t('staff.inviteHint')}
            </p>
            {inviteErrorMessage && (
              <p className="text-sm text-red-600">{inviteErrorMessage}</p>
            )}
          </div>
        </form>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={!!memberToRemove}
        onClose={() => setMemberToRemove(null)}
        title={t('staff.removeTitle')}
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setMemberToRemove(null)}
            >
              {t('staff.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => memberToRemove && removeMutation.mutate(memberToRemove.user.id)}
              isLoading={removeMutation.isPending}
            >
              {t('staff.remove')}
            </Button>
          </>
        }
      >
        <p>
          {t('staff.confirmRemovePrefix')}{' '}
          <strong>{memberToRemove?.user.name || memberToRemove?.user.email}</strong>{' '}
          {t('staff.confirmRemoveSuffix')}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          {t('staff.removeAccessNote')}
        </p>
      </Modal>
    </div>
  )
}
